<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Hooks
	import { onMount } from 'svelte';
	//Formating
	import { format } from '../../../../services/i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import {
		capFirstChar,
		formatSnakeCaseToTitle,
		transformedTimeZones,
	} from '../../../../helpers/utilities';
	import { getNumberInputConfig } from '../../../../helpers/number_input_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { getTimeZoneName, validateGeolocation } from '../../../../helpers/geolocalion_helper';
	//Services
	import { localReq, postReq } from '../../../../services/http';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Maps
	import SelectDeviceLocation from '../../../Maps/SelectDeviceLocation.svelte';
	//Design system components
	import {
		Alert,
		Button,
		Input,
		Loader,
		Radio,
		Select,
		Text,
		Toggle,
		toast,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';
	import { removeOrAddUnderscore } from 'src/helpers/utilities';
	import { coordinatesValidationHelper } from 'src/helpers/coordinates_helpers';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	const id = real_id(device.id),
		settings = devices_settings.getByRealId(id),
		loading = { detect: false, save: false };

	let timeZones = null;
	let timeZone = settings.timezone;
	let cityOffset = null;
	let latitude = settings.lat,
		longitude = settings.lng,
		tzautodetect = settings.tzautodetect,
		daylight = settings.tz_dst_auto ? 'auto' : settings.tz_dst ? 'on' : 'off',
		showModal = false;

	onMount(async () => {
		if (!validateGeolocation(latitude, longitude)) {
			latitude = 0;
			longitude = 0;
		}
		await getTimeZones();
	});

	const getTimeZones = async () => {
		const res: SettingRequest = await postReq(`/timezone/tzoffsetlist`, {});

		if (res.isok) {
			timeZones = transformedTimeZones(Object.keys(res.data));

			timeZone = timeZones?.find((el) => el.stringToSend === timeZone)?.stringToVisualise;

			cityOffset = res.data[removeOrAddUnderscore(timeZone, 'addUnderscore')];
		}
	};

	const saveLocationSettings = async () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			toast.open({
				type: 'danger',
				title: $format('invalid_arguments', { default: 'Invalid arguments' }),
				text: $format('text_invalid_arguments', {
					default:
						"'Latitude' should be greater than -90 and less than 90, 'longitude' should be greater than -180 and less than 180!",
				}),
			});
			return;
		}

		loading.save = true;

		const timezone = timeZones.find((el) => el.stringToVisualise === timeZone).stringToSend;

		const params = {
				tzautodetect,
				latitude,
				longitude,
				timezone,
				tz_utc_offset: cityOffset,
				tz_dst: Number(daylight == 'on'),
				tz_dst_auto: Number(daylight == 'auto'),
			},
			localIP = get_local_ip(id);
		let res: SettingRequest;
		if (localIP) {
			res = await localReq(device, `${localIP}/settings`, 'post', {
				...params,
				id,
				lat: latitude,
				lng: longitude,
			});
		} else {
			res = await postReq(`/device/settings/timezone`, {
				...params,
				id,
				auto_location: Number(tzautodetect),
			});
		}
		if (res.isok) {
			devices_settings.update_devices_settings(device.id, {
				...settings,
				...params,
			});
		}
		onSettingsResponse(res, device, {
			success: $format('location_save_scc_toast', {
				default: 'Location settings saved successfully.',
			}),
			danger: $format('location_save_err_toast', {
				default: 'Failed to save location settings!',
			}),
		});
		loading.save = false;
	};

	const detectLocationSettings = async () => {
		loading.detect = true;
		const res: SettingRequest = await postReq(`/timezone/autodetect`, '');
		if (res.isok) {
			timeZone = removeOrAddUnderscore(res.data.time_zone, 'removeUnderscore'); //'Australia/Broken Hill';
			latitude = res.data.latitude;
			longitude = res.data.longitude;
		} else {
			toast.open({
				type: 'danger',
				title: $format('location_detect_err_toast', {
					default: 'Failed to auto detect time zone!',
				}),
			});
		}
		loading.detect = false;
	};

	const onSelectMapLocation = async (e: CustomEvent) => {
		latitude = Number(e.detail.lat.toFixed(6));
		longitude = Number(e.detail.lng.toFixed(6));
		const newTimeZone = await getTimeZoneName(e.detail.lat, e.detail.lng);
		if (![undefined, 'uninhabited', ''].includes(newTimeZone)) {
			timeZone = newTimeZone;
			await getTimeZones();
		}
	};

	$: {
		if (latitude) latitude = coordinatesValidationHelper(latitude.toString());
		if (longitude) longitude = coordinatesValidationHelper(longitude.toString());
	}
</script>

{#if timeZones == null}
	<Loader size="l" class="d-flex justify-content-center mb-s" />
{:else if timeZones.lenght === 0}
	<Alert
		type="danger"
		class="mb-s"
		heading={$format('device_timezones_error', {
			default: 'Cloud not get device timezones.',
		})}
	/>
{:else}
	<div class="geo-settings">
		<div>
			<Toggle
				size="m"
				bind:checked={tzautodetect}
				id="toggle-geo-auto-detect"
				on:click={saveLocationSettings}
			>
				{$format('automatically_detect_geo_location', {
					default: 'Automatically detect time-zone and Geo location.',
				})}
			</Toggle>

			{#if tzautodetect}
				<Text styleType="label-small" class="ml-xl pl-m text-uppercase text-color-muted">
					{$format('location_time_zone', { default: 'time zone' })}: {timeZone || '—'}
				</Text>
			{/if}
		</div>

		{#if !tzautodetect}
			<div class="width-full">
				<Button
					size="m"
					class="mb-s"
					width="full"
					variant="secondary"
					disabled={loading.save || loading.detect}
					on:click={() => {
						if (
							latitude >= 90 &&
							latitude.toString().includes('.') &&
							latitude.toString().split('.')[1].startsWith('00000')
						) {
							latitude = +latitude.toString().split('.')[0];
						}

						if (
							longitude >= 180 &&
							longitude.toString().includes('.') &&
							longitude.toString().split('.')[1].startsWith('00000')
						) {
							longitude = +longitude.toString().split('.')[0];
						}

						if (!validateGeolocation(latitude, longitude)) {
							latitude = settings.lat;
							longitude = settings.lng;
						}
						showModal = true;
					}}
				>
					{capFirstChar($format('select_map_location'))}
				</Button>
				<Button
					size="m"
					loading={loading.detect}
					width="full"
					disabled={loading.save}
					variant="secondary"
					on:click={detectLocationSettings}
				>
					{capFirstChar($format('button_location_auto_detect'))}
				</Button>
			</div>
			{#if showModal}
				<SelectDeviceLocation
					bind:showModal
					lat={latitude}
					lng={longitude}
					on:change={onSelectMapLocation}
				/>
			{/if}

			<div class="geo-settings-group">
				<Text styleType="body-small">
					{formatSnakeCaseToTitle($format('location_time_zone', { default: 'time zone' }))}:
				</Text>
				<Select
					size="m"
					value={timeZone}
					isAutocomplete
					icon="fas fa-globe"
					options={timeZones.map((t) => t.stringToVisualise)}
					on:change={(e) => (timeZone = e.detail)}
					id="select-xx3bnc9"
					aria-label={$format('location_time_zone', { default: 'time zone' })}
				/>
			</div>

			<div class="geo-settings-group">
				<Text styleType="body-small">
					{$format('daylight_save_time', { default: 'Daylight saving time' })}
				</Text>
				<Radio size="m" bind:selected={daylight} value="auto" id="radio-9d3hm5p"
					>{$format('option_action_auto')}</Radio
				>
				<Radio size="m" bind:selected={daylight} value="on" id="radio-z4u0w0r"
					>{$format('option_action_on')}</Radio
				>
				<Radio size="m" bind:selected={daylight} value="off" id="radio-vidt70a"
					>{$format('option_action_off')}</Radio
				>
			</div>

			<div class="geo-settings-group">
				<Text styleType="body-small">
					{formatSnakeCaseToTitle($format('location_latitude', { default: 'latitude' }))}:
				</Text>
				<Input
					{...getNumberInputConfig()}
					size="m"
					bind:value={latitude}
					min={-90}
					max={90}
					step={0.000001}
					id="input-tyfg9i3"
					aria-label={$format('location_latitude', { default: 'latitude' })}
				/>
			</div>

			<div class="geo-settings-group">
				<Text styleType="body-small">
					{formatSnakeCaseToTitle($format('location_longtitude', { default: 'longitude' }))}:
				</Text>
				<Input
					{...getNumberInputConfig()}
					size="m"
					bind:value={longitude}
					min={-180}
					max={180}
					step={0.000001}
					id="input-045cs65"
					aria-label={$format('location_longtitude', { default: 'longitude' })}
				/>
			</div>

			<div class="geo-settings-save">
				<Button
					size="m"
					loading={loading.save}
					width="full"
					disabled={loading.detect ||
						(tzautodetect === $settingsStore?.tzautodetect && tzautodetect)}
					on:click={saveLocationSettings}
				>
					{$format('button_save')}
				</Button>
			</div>
		{/if}
	</div>
{/if}

<style>
	.geo-settings {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	.geo-settings > div {
		margin-bottom: 12px;
	}

	.geo-settings-save {
		margin-top: 4px;
		width: 100%;
	}

	.geo-settings-group {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		width: 100%;
	}
</style>
